import AddCargoOperationPage from "@features/drybulk-cargo-operations/components/AddCargoOperationPage";
import DrybulkCargoOperationDetail from "@features/drybulk-cargo-operations/components/DrybulkCargoOperationDetail";
import DrybulkCargoOperationsList from "@features/drybulk-cargo-operations/components/DrybulkCargoOperationsList";
import ImportDeferredPage from "@features/drybulk-cargo-operations/components/ImportDeferredPage";
import SequenceDetailsPage from "@features/drybulk-cargo-operations/components/SequenceDetailsPage";
import { DRYBULK_CARGO_OPS_ROUTE_SEGMENTS } from "@features/drybulk-cargo-operations/constants";
import CreateVesselCallContainer from "@features/vessel-call/create/CreateVesselCallContainer";
import CallAdditionalDocuments from "@features/vessel-call/create/steps/CallAdditionalDocuments/CallAdditionalDocuments";
import ImoFAL from "@features/vessel-call/create/steps/ImoFAL/ImoFAL";
import ImoFALRenderer from "@features/vessel-call/create/steps/ImoFAL/ImoFALRenderer";
import PreArrivalNotification from "@features/vessel-call/create/steps/PreArrivalNotification/PreArrivalNotification";
import DepartureContainer from "@features/vessel-call/vessel-call-status/Departure/DepartureContainer";
import AddMovementService from "@features/vessel-call/vessel-call-status/MovementServices/form/AddMovementService";
import Movements from "@features/vessel-call/vessel-call-status/MovementServices/grid/Movements";
import MovementServicesContainer from "@features/vessel-call/vessel-call-status/MovementServices/MovementServicesContainer";
import MovementServicePreview from "@features/vessel-call/vessel-call-status/MovementServices/preview/MovementServicePreview";
import OperationsDisputes from "@features/vessel-call/vessel-call-status/Operations/grid/OperationsDisputes";
import OperationsLiquidBulk from "@features/vessel-call/vessel-call-status/Operations/grid/OperationsLiquidBulk";
import CargoOperations from "@features/vessel-call/vessel-call-status/Operations/grid/OperationsLiquidBulkSteps/cargoOperations";
import PldaGrid from "@features/vessel-call/vessel-call-status/Operations/grid/OperationsLiquidBulkSteps/pldaGrid";
import OperationsContainer from "@features/vessel-call/vessel-call-status/Operations/OperationsContainer";
import AddPortService from "@features/vessel-call/vessel-call-status/PortServices/form/AddPortService";
import PortServices from "@features/vessel-call/vessel-call-status/PortServices/grid/PortServices";
import PortServicesContainer from "@features/vessel-call/vessel-call-status/PortServices/PortServicesContainer";
import PortServicePreview from "@features/vessel-call/vessel-call-status/PortServices/preview/PortServicePreview";
import ShippingAgencyTransfer from "@features/vessel-call/vessel-call-status/ShippingAgencyTransfer/ShippingAgencyTransfer";
import VesselCallDetails from "@features/vessel-call/vessel-call-status/VesselCallDetails/VesselCallDetails";
import VesselCallStatus from "@features/vessel-call/vessel-call-status/VesselCallStatus";
import VesselScanPage from "@features/vessel-call/vessel-scan/VesselScanPage";
import VesselScanResults from "@features/vessel-call/vessel-scan/VesselScanResults";
import VesselCallContainer from "@features/vessel-call/VesselCallContainer";
import VesselCallPage from "@pages/vessel-call/VesselCallPage";
import { paths } from "@utils/navigation";
import { type AppRoute } from "../types";

export const getVesselCallRoutes = (): AppRoute => ({
  path: paths.vesselCall.index,
  protected: true,
  element: <VesselCallPage />,
  requiredPermissions: ["hasVesselCallAccess"],
  children: [
    { index: true, element: <VesselCallContainer /> },
    { path: paths.vesselCall.vesselScan, element: <VesselScanPage /> },
    { path: paths.vesselCall.scanResults, element: <VesselScanResults /> },
    {
      path: paths.vesselCall.create,
      element: <CreateVesselCallContainer />,
      children: [
        { index: true, redirectTo: paths.vesselCall.preArrivalNotification },
        { path: paths.vesselCall.preArrivalNotification, element: <PreArrivalNotification /> },
        {
          path: paths.vesselCall.imoFAL,
          element: <ImoFAL />,
          children: [{ path: ":type", element: <ImoFALRenderer /> }],
        },
        { path: paths.vesselCall.additionalDocuments, element: <CallAdditionalDocuments /> },
      ],
    },
    {
      path: paths.vesselCall.vesselCallStatus,
      element: <VesselCallStatus />,
      children: [
        { index: true, redirectTo: paths.vesselCall.vesselDetails },
        { path: paths.vesselCall.vesselDetails, element: <VesselCallDetails /> },
        {
          path: paths.vesselCall.movementServices,
          element: <MovementServicesContainer />,
          children: [
            { index: true, element: <Movements /> },
            { path: paths.vesselCall.addMovementService, element: <AddMovementService /> },
            {
              path: `:subModuleId`,
              element: <MovementServicePreview />,
            },
          ],
        },
        {
          path: paths.vesselCall.portServices,
          element: <PortServicesContainer />,
          children: [
            { index: true, element: <PortServices /> },
            { path: paths.vesselCall.addPortService, element: <AddPortService /> },
            {
              path: `:subModuleId`,
              element: <PortServicePreview />,
            },
          ],
        },
        {
          path: paths.vesselCall.operations.index,
          element: <OperationsContainer />,
          children: [
            { index: true, redirectTo: paths.vesselCall.operations.disputes },
            { path: paths.vesselCall.operations.disputes, element: <OperationsDisputes /> },
            {
              path: paths.vesselCall.operations.liquidBulk,
              element: <OperationsLiquidBulk />,
              children: [
                { index: true, redirectTo: paths.vesselCall.operations.plda },
                { path: paths.vesselCall.operations.plda, element: <PldaGrid /> },
                {
                  path: paths.vesselCall.operations.cargoOperations,
                  element: <CargoOperations />,
                },
              ],
            },
            {
              path: paths.vesselCall.operations.dryBulk,
              children: [
                { index: true, element: <DrybulkCargoOperationsList /> },
                {
                  path: ":operationId",
                  element: <DrybulkCargoOperationDetail />,
                  children: [
                    {
                      path: `${DRYBULK_CARGO_OPS_ROUTE_SEGMENTS.sequence}/:sequenceId`,
                      element: <SequenceDetailsPage />,
                    },
                  ],
                },
                {
                  path: DRYBULK_CARGO_OPS_ROUTE_SEGMENTS.addOperation,
                  element: <AddCargoOperationPage />,
                },
                {
                  path: DRYBULK_CARGO_OPS_ROUTE_SEGMENTS.importDeferred,
                  element: <ImportDeferredPage />,
                },
              ],
            },
          ],
        },
        { path: paths.vesselCall.departure, element: <DepartureContainer /> },
        { path: paths.vesselCall.shippingAgencyTransfer, element: <ShippingAgencyTransfer /> },
      ],
    },
  ],
});
